import React from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { Button } from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Lock, Crown, Zap } from 'lucide-react';

interface FeatureGuardProps {
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
  requiredPlan?: 'pro' | 'enterprise';
  usageLimit?: {
    metric: string;
    current: number;
    limit: number;
  };
}

interface UpgradePromptProps {
  feature: string;
  requiredPlan?: 'pro' | 'enterprise';
  usageLimit?: {
    metric: string;
    current: number;
    limit: number;
  };
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ feature, requiredPlan, usageLimit }) => {
  const { currentPlan } = useSubscription();
  
  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'pro': return 'Pro';
      case 'enterprise': return 'Enterprise';
      default: return plan;
    }
  };

  const getFeatureDisplayName = (feature: string) => {
    switch (feature) {
      case 'reviews': return 'Document Reviews';
      case 'advanced_analytics': return 'Advanced Analytics';
      case 'slack_integration': return 'Slack Integration';
      case 'custom_branding': return 'Custom Branding';
      case 'api_access': return 'API Access';
      case 'priority_support': return 'Priority Support';
      default: return feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <CardTitle className="text-lg">Feature Locked</CardTitle>
        <CardDescription>
          {usageLimit ? (
            `You've reached your limit of ${usageLimit.limit} ${getFeatureDisplayName(feature).toLowerCase()}.`
          ) : (
            `${getFeatureDisplayName(feature)} is available on ${getPlanDisplayName(requiredPlan || 'pro')} plans and above.`
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {usageLimit && (
          <div className="flex items-center justify-between text-sm">
            <span>Current usage:</span>
            <span className="font-medium">{usageLimit.current} / {usageLimit.limit}</span>
          </div>
        )}
        
        {currentPlan && (
          <div className="flex items-center justify-between text-sm">
            <span>Current plan:</span>
            <Badge variant="outline">{currentPlan.display_name}</Badge>
          </div>
        )}
        
        <div className="flex gap-2">
          <Button className="flex-1" variant="outline">
            Learn More
          </Button>
          <Button className="flex-1">
            Upgrade Plan
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const FeatureGuard: React.FC<FeatureGuardProps> = ({ 
  feature, 
  children, 
  fallback,
  showUpgradePrompt = true,
  requiredPlan,
  usageLimit
}) => {
  const { hasFeature, isLoading, currentPlan } = useSubscription();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // TEMPORARILY DISABLED: All features are enabled
  const hasAccess = true; // hasFeature(feature);
  
  // TEMPORARILY DISABLED: All plans are allowed
  const hasRequiredPlan = true; // !requiredPlan || 
    // (currentPlan && (
    //   requiredPlan === 'pro' ? ['pro', 'enterprise'].includes(currentPlan.name) :
    //   requiredPlan === 'enterprise' ? currentPlan.name === 'enterprise' :
    //   true
    // ));

  if (!hasAccess || !hasRequiredPlan) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    if (showUpgradePrompt) {
      return (
        <UpgradePrompt 
          feature={feature} 
          requiredPlan={requiredPlan}
          usageLimit={usageLimit}
        />
      );
    }
    
    return null;
  }

  return <>{children}</>;
};

// Convenience components for common features
export const ReviewsGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ children, fallback }) => (
  <FeatureGuard feature="reviews" fallback={fallback}>
    {children}
  </FeatureGuard>
);

export const AnalyticsGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ children, fallback }) => (
  <FeatureGuard feature="advanced_analytics" fallback={fallback}>
    {children}
  </FeatureGuard>
);

export const SlackGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ children, fallback }) => (
  <FeatureGuard feature="slack_integration" fallback={fallback}>
    {children}
  </FeatureGuard>
);

export const BrandingGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ children, fallback }) => (
  <FeatureGuard feature="custom_branding" fallback={fallback}>
    {children}
  </FeatureGuard>
);

export const ApiGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ children, fallback }) => (
  <FeatureGuard feature="api_access" fallback={fallback}>
    {children}
  </FeatureGuard>
);

// Usage-based guard
export const UsageGuard: React.FC<{
  feature: string;
  metric: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ feature, metric, children, fallback }) => {
  const { getCurrentUsage, getLimit, isLoading } = useSubscription();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentUsage = getCurrentUsage(metric);
  const limit = getLimit(metric);
  const hasReachedLimit = limit !== -1 && currentUsage >= limit;

  if (hasReachedLimit) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <UpgradePrompt 
        feature={feature}
        usageLimit={{ metric, current: currentUsage, limit }}
      />
    );
  }

  return <>{children}</>;
}; 